<template>
  <div>
    <v-btn
      color="primary"
      @click="triggerFileInput"
      :loading="loading"
      density="compact"
      prepend-icon="mdi-file-upload-outline"
    >
    </v-btn>

    <v-file-input
      ref="fileInput"
      v-model="file"
      accept=".xlsx,.xls"
      style="display: none"
      @change="handleFileUpload"
    />

    <!-- Dialog de choix de démarque pour les soldes -->
    <v-dialog v-model="soldeDialog" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="text-h5">Choix de la démarque - Fichier Soldes</span>
        </v-card-title>
        <v-card-text>
          <p class="mb-4">Veuillez sélectionner la démarque à appliquer :</p>
          <v-radio-group v-model="selectedDemarque" :rules="[v => !!v || 'Veuillez sélectionner une démarque']">
            <v-radio label="VP (Prix de vente)" value="VP"></v-radio>
            <v-radio label="D1 (Première démarque)" value="D1"></v-radio>
            <v-radio label="D2 (Deuxième démarque)" value="D2"></v-radio>
            <v-radio label="D3 (Troisième démarque)" value="D3"></v-radio>
            <v-radio label="OT (Autre démarque)" value="OT"></v-radio>
          </v-radio-group>
          
          <v-alert
            v-if="validationError"
            type="error"
            variant="tonal"
            class="mt-4"
          >
            {{ validationError }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" variant="text" @click="cancelSoldeImport">
            Annuler
          </v-btn>
          <v-btn color="primary" variant="elevated" @click="confirmSoldeImport" :loading="soldeLoading">
            Valider et importer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog de progression -->
    <v-dialog v-model="dialog" max-width="500px">
      <v-card>
        <v-card-title>
          <span class="text-h5">Importation en cours</span>
        </v-card-title>
        <v-card-text>
          <v-progress-linear
            :model-value="progress"
            :indeterminate="progress === 0"
            color="primary"
            height="25"
          >
            <template v-slot:default="{ value }">
              <strong>{{ Math.ceil(value) }}%</strong>
            </template>
          </v-progress-linear>
          <p class="mt-2">{{ progressMessage }}</p>
          <!-- Affichage de la démarque sélectionnée -->
          <v-chip 
            v-if="selectedDemarqueDisplay" 
            color="primary" 
            variant="tonal"
            class="mt-2"
          >
            Démarque sélectionnée : {{ selectedDemarqueDisplay }}
          </v-chip>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Notification -->
    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      timeout="5000"
      location="top"
    >
      {{ snackbarMessage }}
      <template v-slot:actions>
        <v-btn color="white" variant="text" @click="snackbar = false">
          Fermer
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import store from '../store'
import * as XLSX from 'xlsx'

const file = ref(null)
const fileInput = ref(null)
const loading = ref(false)
const dialog = ref(false)
const progress = ref(0)
const progressMessage = ref('')
const snackbar = ref(false)
const snackbarMessage = ref('')
const snackbarColor = ref('success')

// Variables pour la gestion des soldes
const soldeDialog = ref(false)
const soldeLoading = ref(false)
const selectedDemarque = ref('')
const validationError = ref('')
let pendingExcelData = null

// Affichage de la démarque sélectionnée
const selectedDemarqueDisplay = computed(() => {
  const demarqueMap = {
    'VP': 'VP (Prix de vente)',
    'D1': 'D1 (Première démarque)',
    'D2': 'D2 (Deuxième démarque)',
    'D3': 'D3 (Troisième démarque)',
    'OT': 'OT (Autre démarque)'
  }
  return demarqueMap[selectedDemarque.value] || ''
})

// Colonnes obligatoires pour chaque type
const PROMO_COLUMNS = ['REFERENCE', 'CODE_BARRE', 'PVC', 'PROMO', 'PRIX_PROMO', 'PERIODE_PROMO']
const SOLDE_COLUMNS = ['REFERENCE', 'CODE_BARRE', 'PVC', 'VP', 'D1%', 'D1€', 'D2%', 'D2€', 'D3%', 'D3€', 'OT%', 'OT€']

const triggerFileInput = () => {
  fileInput.value?.click()
}

const handleFileUpload = async (event) => {
  const uploadedFile = event.target.files[0]
  if (!uploadedFile) return

  try {
    loading.value = true
    progress.value = 0
    progressMessage.value = 'Lecture du fichier...'

    const data = await readExcelFile(uploadedFile)
    
    // Détecter le type de fichier
    const fileType = detectFileType(data)
    // console.log(fileType);
    
    if (fileType === 'promo') {
      // Traitement direct pour les promos
      await processPromoFile(data)
    } else if (fileType === 'solde') {
      // Ouvrir le dialog pour choisir la démarque
      pendingExcelData = data
      soldeDialog.value = true
      selectedDemarque.value = ''
      validationError.value = ''
    } else {
      throw new Error('Format de fichier non reconnu. Veuillez vérifier les colonnes.')
    }

  } catch (error) {
    console.error('Erreur lors de l\'importation:', error)
    showSnackbar(`Erreur: ${error.message}`, 'error')
    dialog.value = false
    loading.value = false
  } finally {
    if (!soldeDialog.value) {
      file.value = null
      if (fileInput.value) {
        fileInput.value.reset()
      }
    }
  }
}

const detectFileType = (data) => {
  if (data.length === 0) {
    throw new Error('Le fichier est vide')
  }

  const headers = Object.keys(data[0])
  
  // Vérifier les colonnes promo
  const hasPromoColumns = PROMO_COLUMNS.every(col => 
    headers.some(h => h.trim().toUpperCase() === col)
  )
  
  if (hasPromoColumns) {
    return 'promo'
  }
  
  // Vérifier les colonnes soldes
  const hasSoldeColumns = SOLDE_COLUMNS.every(col => 
    headers.some(h => h.trim().toUpperCase() === col)
  )
  
  if (hasSoldeColumns) {
    return 'solde'
  }
  
  // Trouver les colonnes manquantes pour un message plus précis
  const missingPromo = PROMO_COLUMNS.filter(col => 
    !headers.some(h => h.trim().toUpperCase() === col)
  )
  
  const missingSolde = SOLDE_COLUMNS.filter(col => 
    !headers.some(h => h.trim().toUpperCase() === col)
  )
  
  let errorMsg = 'Colonnes manquantes : '
  if (missingPromo.length < PROMO_COLUMNS.length) {
    errorMsg += `\nPour un fichier PROMO : ${missingPromo.join(', ')}`
  }
  if (missingSolde.length < SOLDE_COLUMNS.length) {
    errorMsg += `\nPour un fichier SOLDE : ${missingSolde.join(', ')}`
  }
  
  throw new Error(errorMsg)
}

const processPromoFile = async (data) => {
  dialog.value = true
  progress.value = 30
  progressMessage.value = 'Traitement du fichier promo...'
  
  await updateArticles(data, 'promo')
  
  progress.value = 100
  progressMessage.value = 'Importation terminée avec succès !'
  
  setTimeout(() => {
    dialog.value = false
    loading.value = false
  }, 1000)
  
  showSnackbar('Importation des promos réussie !', 'success')
}

const confirmSoldeImport = async () => {
  if (!selectedDemarque.value) {
    validationError.value = 'Veuillez sélectionner une démarque'
    return
  }
  
  validationError.value = ''
  soldeLoading.value = true
  
  try {
    soldeDialog.value = false
    dialog.value = true
    progress.value = 30
    progressMessage.value = `Traitement du fichier soldes avec la démarque ${selectedDemarqueDisplay.value}...`
    
    await updateArticles(pendingExcelData, 'solde', selectedDemarque.value)
    
    progress.value = 100
    progressMessage.value = `Importation terminée avec succès ! (Démarque: ${selectedDemarqueDisplay.value})`
    
    setTimeout(() => {
      dialog.value = false
      loading.value = false
    }, 1000)
    
    showSnackbar(`Importation des soldes (${selectedDemarqueDisplay.value}) réussie !`, 'success')
  } catch (error) {
    console.error('Erreur lors de l\'importation des soldes:', error)
    showSnackbar(`Erreur: ${error.message}`, 'error')
    dialog.value = false
    loading.value = false
  } finally {
    soldeLoading.value = false
    pendingExcelData = null
    file.value = null
    if (fileInput.value) {
      fileInput.value.reset()
    }
  }
}

const cancelSoldeImport = () => {
  soldeDialog.value = false
  pendingExcelData = null
  selectedDemarque.value = ''
  loading.value = false
  file.value = null
  if (fileInput.value) {
    fileInput.value.reset()
  }
}

const readExcelFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result)
        const workbook = XLSX.read(data, { type: 'array' })
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]]
        const jsonData = XLSX.utils.sheet_to_json(firstSheet)
        resolve(jsonData)
      } catch (error) {
        reject(new Error('Erreur de lecture du fichier Excel'))
      }
    }
    
    reader.onerror = () => {
      reject(new Error('Erreur de lecture du fichier'))
    }
    
    reader.readAsArrayBuffer(file)
  })
}
let allDemarques=ref([])
let demarqueStats=ref()
const updateArticles = async (excelData, fileType, demarque = null) => {
  store.state.mode='import'
  const articles = ref(store.state.articles)
  let updatedCount = 0
  let totalRows = excelData.length
  // Liste des démarques disponibles
  for (let i = 0; i < excelData.length; i++) {
    if(excelData[i]['CODE_BARRE']!==''){
        const row = excelData[i]
    // Récupérer les valeurs communes
    const codeMarque = String(row.CODE_MARQUE || row['CODE_MARQUE'] || '').trim()
    const codeTheme = String(row.CODE_THEME || row['CODE_THEME'] || '').trim()
    const reference = String(row.REFERENCE || row['REFERENCE'] || '').trim()
    const codeBarre = String(row.CODE_BARRE || row['CODE_BARRE'] || '').trim()
    const prixBase = parseFloat(row.PVC || row['PVC'] || 0)
    // Vérifier si l'article existe dans le store
    if (articles.value[codeMarque] && 
        articles.value[codeMarque][codeTheme] && 
        articles.value[codeMarque][codeTheme][reference] &&
        articles.value[codeMarque][codeTheme][reference][codeBarre]){
            const article = articles.value[codeMarque][codeTheme][reference][codeBarre]
            // Mettre à jour vte_euro (PRIX_BASE)
            if (prixBase > 0) {
                article.vte_euro = String(prixBase) 
            }
            article.soldes = {}
            if (fileType === 'promo') {
                // Traitement pour les promos
                const promo = parseFloat(row.PROMO || row['PROMO'] || 0)
                const prixPromo = parseFloat(row.PRIX_PROMO || row['PRIX_PROMO'] || 0)
                const periodePromo = String(row.PERIODE_PROMO || row['PERIODE_PROMO'] || '').trim()
                if (periodePromo!=''&&!article.soldes[article.type_promo]) {
                    article.soldes[periodePromo] = {}
                }
                if (promo > 0 && prixPromo > 0) {
                // const remise = ((prixBase - prixPromo) / prixBase) * 100
                    article.promo = String(promo)
                    article.prix_promo = prixPromo
                    article.type_promo = periodePromo
                    article.imported = true
                    article.soldes[article.type_promo] = {
                        promo: String(Math.round(((article.vte_euro - prixPromo) / article.vte_euro) * 100)),
                        prix_promo: prixPromo,
                        type_promo: periodePromo,
                        prix_base: article.vte_euro
                    }
                } else if (promo === 0) {
                    article.promo = ''
                    article.prix_promo = ''
                    article.type_promo = ''
                    article.imported = true
                // delete article.soldes[article.type_promo]
                }

            } else if (fileType === 'solde') {
                allDemarques.value = ['VP', 'D1', 'D2', 'D3', 'OT']
                
                // Statisques pour chaque démarque
                demarqueStats.value = {}
                allDemarques.value.forEach(d => {
                    demarqueStats.value[d] = 0
                })
                // Récupérer les valeurs pour chaque démarque
                const vp = parseFloat(row.VP || row['VP'] || 0)
                const d1 = parseFloat(row['D1€'] || row['D1€'] || 0)
                const d2 = parseFloat(row['D2€'] || row['D2€'] || 0)
                const d3 = parseFloat(row['D3€'] || row['D3€'] || 0)
                const ot = parseFloat(row['OT€'] || row['OT€'] || 0)
                article.imported = true
                // Initialiser l'objet soldes si nécessaire
                if (!article.soldes) {
                article.soldes = {}
                }

                // Calculer pour chaque démarque et stocker dans article.soldes
                const demarquesData = {
                'VP': {
                    prixPromo: Math.round((prixBase - (prixBase * vp )) * 10) / 10,
                    typePromo: 'SOLVP',
                    pourcentage: vp
                },
                'D1': {
                    prixPromo: d1,
                    typePromo: 'SOLD1',
                    pourcentage: parseFloat(row['D1%'] || row['D1%'] || 0)
                },
                'D2': {
                    prixPromo: d2,
                    typePromo: 'SOLD2',
                    pourcentage: parseFloat(row['D2%'] || row['D2%'] || 0)
                },
                'D3': {
                    prixPromo: d3,
                    typePromo: 'SOLD3',
                    pourcentage: parseFloat(row['D3%'] || row['D3%'] || 0)
                },
                'OT': {
                    prixPromo: ot,
                    typePromo: 'EXTRA',
                    pourcentage: parseFloat(row['OT%'] || row['OT%'] || 0)
                }
                }

                // Stocker chaque démarque dans article.soldes
                allDemarques.value.forEach(d => {
                const demarqueInfo = demarquesData[d]
                const prixPromo = demarqueInfo.prixPromo
                
                // Mettre à jour ou créer l'entrée pour cette démarque
                if (!article.soldes[d]) {
                    article.soldes[d] = {}
                }
                
                if (prixPromo > 0 && prixPromo < prixBase) {
                    const remise = ((prixBase - prixPromo) / prixBase) * 100
                    article.soldes[d] = {
                    promo: String(Math.round(remise)),
                    prix_promo: prixPromo,
                    type_promo: demarqueInfo.typePromo,
                    pourcentage: demarqueInfo.pourcentage,
                    prix_base: prixBase
                    }
                    demarqueStats.value[d]++
                } else {
                    // Si pas de solde valide, supprimer l'entrée
                    delete article.soldes[d]
                }
                })

                // Appliquer la démarque sélectionnée à l'article principal
                const selectedDemarqueData = demarquesData[demarque]
                if (selectedDemarqueData) {
                const prixPromo = selectedDemarqueData.prixPromo
                const typePromo = selectedDemarqueData.typePromo

                if (prixPromo > 0 && prixPromo < prixBase) {
                    const remise = ((prixBase - prixPromo) / prixBase) * 100
                    article.promo = String(Math.round(remise))
                    article.prix_promo = prixPromo
                    article.type_promo = typePromo
                } else {
                    article.promo = ''
                    article.prix_promo = ''
                    article.type_promo = ''
                }
                }

                
            }
        }
        updatedCount++
    // Mettre à jour la progression
    progress.value = 30 + ((i + 1) / totalRows) * 70
    }
    
  }

  // Sauvegarder les modifications dans le store
//   console.log(articles);
//   console.log(articles);
  
  store.commit('addArticles', articles)
  
  // Message avec les statistiques
  if (fileType === 'solde') {
    let statsMessage = `Démarque ${selectedDemarqueDisplay.value} appliquée sur ${updatedCount} articles.\n`
    statsMessage += 'Autres démarques disponibles :\n'
    allDemarques.value.forEach(d => {
      if (d !== demarque) {
        statsMessage += `- ${d}: ${demarqueStats.value[d]} articles calculés\n`
      }
    })
    progressMessage.value += `\n${statsMessage}`
  }

  progressMessage.value = `${updatedCount} articles mis à jour sur ${totalRows}`
  
  if (updatedCount === 0) {
    throw new Error('Aucun article trouvé dans le store')
  }
}

const showSnackbar = (message, color = 'success') => {
  snackbarMessage.value = message
  snackbarColor.value = color
  snackbar.value = true
}
</script>

<style scoped>
/* Styles optionnels */
</style>